// Reason classifier output + UI formatter for Alpha Zone entries / exits.
//
// The classifier itself lives in scripts/analytics/compute-zone-changes.ts —
// it writes ZoneEvent rows to data/zone-changes.json. This module owns the
// shared schema + the single string formatter used by the leaderboard so the
// UI never has to switch on `reason` directly.

export type ZoneReason =
  | 'q_gate_cleared'      // entry: Q crossed from <0 to ≥0
  | 'q_gate_failed'       // exit:  Q crossed from ≥0 to <0
  | 'composite_improved'  // entry: own composite Δ ≥ +0.15
  | 'composite_fell'      // exit:  own composite Δ ≤ −0.15
  | 'displaced_outgoing'  // entry: own composite ~flat, named symbol fell off boundary
  | 'displaced_incoming'  // exit:  own composite ~flat, named symbol pushed in
  | 'baseline';           // first snapshot in lookback window — no causal info

export interface PillarDelta {
  profitability?: number;
  growth?: number;
  safety?: number;
  shareholderReturns?: number;
}

export interface ZoneEvent {
  symbol: string;
  name: string;
  date: string;                // YYYY-MM-DD
  daysInZone?: number;         // exits only
  reason: ZoneReason;
  deltaComposite?: number;     // own change since prev snapshot
  deltaQ?: number;
  deltaV?: number;             // V component change
  pillarDelta?: PillarDelta;   // Q pillar-level breakdown
  topDriver?: 'Q' | 'V' | 'both'; // what drove the composite change
  topPillar?: string;          // highest-moving Q pillar if Q-driven
  priorComposite?: number;
  currComposite?: number;
  counterparty?: string;       // symbol on the other side of a displacement
  gapDays?: number;            // when prev snapshot was >1 calendar day back
}

const fmtDelta = (d: number | undefined): string => {
  if (d == null || !Number.isFinite(d)) return '';
  return `${d >= 0 ? '+' : ''}${d.toFixed(2)}`;
};

/**
 * One-line, dense, scan-friendly description of why a stock entered or exited
 * the Alpha Zone. Returned strings stay short enough to fit under a ticker in
 * a leaderboard row.
 */
export function formatZoneReason(e: ZoneEvent): string {
  const gap = e.gapDays && e.gapDays > 1 ? ` (over ${e.gapDays}d)` : '';
  switch (e.reason) {
    case 'q_gate_cleared': {
      const q = e.deltaQ;
      const detail = q != null && Number.isFinite(q) ? ` (Q ${fmtDelta(q)})` : '';
      return `Q gate cleared${detail}${gap}`;
    }
    case 'q_gate_failed': {
      const q = e.deltaQ;
      const detail = q != null && Number.isFinite(q) ? ` (Q ${fmtDelta(q)})` : '';
      return `Q gate failed${detail}${gap}`;
    }
    case 'composite_improved': {
      if (e.topDriver === 'Q' && e.topPillar) {
        return `Q ${fmtDelta(e.deltaQ)} · ${e.topPillar} led${gap}`;
      }
      if (e.topDriver === 'V') {
        return `V ${fmtDelta(e.deltaV)} drove composite${gap}`;
      }
      if (e.topDriver === 'both' && e.topPillar) {
        return `Q ${fmtDelta(e.deltaQ)} · V ${fmtDelta(e.deltaV)} · ${e.topPillar} led${gap}`;
      }
      return `Composite ${fmtDelta(e.deltaComposite)}${gap}`;
    }
    case 'composite_fell': {
      if (e.topDriver === 'Q' && e.topPillar) {
        return `Q ${fmtDelta(e.deltaQ)} · ${e.topPillar} fell${gap}`;
      }
      if (e.topDriver === 'V') {
        return `V ${fmtDelta(e.deltaV)} drove composite${gap}`;
      }
      if (e.topDriver === 'both' && e.topPillar) {
        return `Q ${fmtDelta(e.deltaQ)} · V ${fmtDelta(e.deltaV)} · ${e.topPillar} fell${gap}`;
      }
      return `Composite ${fmtDelta(e.deltaComposite)}${gap}`;
    }
    case 'displaced_outgoing':
      return `Displaced ${e.counterparty ?? '—'}${gap}`;
    case 'displaced_incoming':
      return `Displaced by ${e.counterparty ?? '—'}${gap}`;
    case 'baseline':
      return 'In zone at window start';
  }
}
